import { ButtonType, Maybe } from '@varo/graphql-schema-types';
import {
  ImageSourcePropType,
  Platform,
  Pressable,
  StyleSheet,
  View,
} from 'react-native';
import IconAvatar from 'shared/components/vxl/avatar/IconAvatar/IconAvatar';
import Button from 'shared/components/vxl/Button/Button';
import { Tag, TagType } from 'shared/components/vxl/Tag/Tag';
import BodyBaseBoldText from 'shared/components/vxl/typography/primary/body/BodyBaseBoldText';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import {
  PressableAnalyticsProps,
  usePressableAnalytics,
} from 'shared/hooks/analytics/usePressableAnalytics';
import theme from 'shared/styles/theme';

interface StandardOfferCardProps extends PressableAnalyticsProps {
  id: string;
  titleText: string;
  captionText: string;
  distanceText?: string;
  isLocationBased?: boolean;
  logoImage?: ImageSourcePropType;
  isDisabled?: boolean;
  isActivatedOffer?: boolean;
  activatedDurationText?: string;
  onPress?: () => void;
  accessibilityLabel?: string;
  cta?: {
    button?: {
      label: string;
      onPress?: () => void;
      analyticsId?: string;
      customAttributes?: Maybe<Record<string, unknown>>;
    };
    link?: {
      label: string;
      onPress?: () => void;
    };
  };
}

const TITLE_TEXT_LINE_LIMIT = 1;
const CAPTION_TEXT_LINE_LIMIT = 1;

export const StandardOfferCard = ({
  titleText,
  captionText,
  distanceText,
  isLocationBased = true,
  logoImage,
  onPress,
  isDisabled = false,
  isActivatedOffer = false,
  activatedDurationText,
  analyticsId = '',
  customAttributes = null,
  accessibilityLabel,
  cta,
}: StandardOfferCardProps) => {
  const cardPressAnalytics = usePressableAnalytics({
    analyticsId,
    customAttributes,
  });

  const handleOnPress = () => {
    cardPressAnalytics();
    onPress?.();
  };

  return (
    <Pressable
      style={({ pressed, hovered, focused }) => [
        styles.mainView,
        pressed ? styles.pressedStyle : null,
        !pressed && hovered ? styles.hover : null,
        focused && !hovered && !pressed ? styles.focus : null,
        isDisabled ? styles.disabledStyle : null,
      ]}
      onPress={handleOnPress}
      disabled={isDisabled}
      accessible
      accessibilityLabel={accessibilityLabel || titleText}
      accessibilityState={{ disabled: isDisabled }}
      // On web a role="button" renders a <button>, which cannot legally contain
      // the nested CTA <Button> elements. Keep the button role on native only.
      role={Platform.OS === 'web' ? undefined : 'button'}
      testID="standard-offer-card"
    >
      {isLocationBased && distanceText ? (
        <View style={styles.tagContainer}>
          <Tag text={distanceText} type={TagType.INFO} />
        </View>
      ) : null}

      <View style={styles.contentView}>
        <IconAvatar
          variant="MD"
          iconKey={!logoImage ? 'STORE' : undefined}
          imageSource={logoImage}
          defaultPlaceholderIcon="STORE"
          iconAccessibilityLabel="store-icon"
        />

        <View style={styles.bottomSection}>
          <View style={styles.textContainer}>
            {titleText ? (
              <BodyBaseBoldText
                style={isDisabled ? styles.disabledStyle : styles.text}
                numberOfLines={TITLE_TEXT_LINE_LIMIT}
              >
                {titleText}
              </BodyBaseBoldText>
            ) : null}
            {captionText ? (
              <BodySmText
                style={isDisabled ? styles.disabledStyle : styles.text}
                numberOfLines={CAPTION_TEXT_LINE_LIMIT}
              >
                {captionText}
              </BodySmText>
            ) : null}
          </View>

          <View style={styles.ctaArea}>
            {isActivatedOffer && activatedDurationText ? (
              <Tag
                text={activatedDurationText}
                type={TagType.SUCCESS}
                iconKey="CLOCK_FILLED"
              />
            ) : null}

            {cta?.button && !isActivatedOffer ? (
              <Button
                label={cta.button.label}
                type="SECONDARY"
                size="SMALL"
                width="HUG"
                onPress={cta.button.onPress}
                analyticsId={cta.button.analyticsId}
                customAttributes={cta.button.customAttributes}
              />
            ) : null}

            {cta?.link ? (
              <Button
                type={ButtonType.Text}
                label={cta.link.label}
                onPress={cta.link.onPress}
              />
            ) : null}
          </View>
        </View>
      </View>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  contentView: {
    alignItems: 'center',
    flexDirection: 'column',
    gap: theme.spacing.m,
    padding: theme.spacing.m,
    width: '100%',
  },
  bottomSection: {
    alignItems: 'center',
    flexDirection: 'column',
    gap: 12,
    width: '100%',
  },
  ctaArea: {
    alignItems: 'center',
    height: 36,
    justifyContent: 'center',
  },
  disabledStyle: {
    color: theme.color.interactive.disabled.text,
  },
  focus: {
    backgroundColor: theme.color.background.primary,
    borderColor: theme.color.interactive.focused.border,
    borderWidth: 1,
  },
  hover: {
    backgroundColor: theme.color.interactive.secondaryHovered,
  },
  mainView: {
    alignItems: 'center',
    backgroundColor: theme.color.background.primary,
    borderRadius: 4,
    width: 160,
  },
  pressedStyle: {
    backgroundColor: theme.color.interactive.secondaryPressed,
  },
  tagContainer: {
    position: 'absolute',
    right: theme.spacing.m,
    top: theme.spacing.m,
  },
  text: {
    textAlign: 'center',
  },
  textContainer: {
    alignItems: 'center',
    width: '100%',
  },
});

export default StandardOfferCard;
